import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRbacService } from "../apis/rbac/useRbacService";
import {
  TRbacMatrix,
  TRoleWithPermissions,
  TCreateRoleRequest,
  TUpdateRolePermissionsRequest,
  TRoleUsersResponse,
} from "../apis/rbac/rbac.interfaces";
import type { IRbac } from "../apis/rbac/rbac.interfaces";
import { IServerResponse } from "../apis/common.interfaces";
import { useInstituteContext } from "../context/institute.context";

export type UseRbacHookParams = {
  selectedRoleId?: string | null;
};

export const useRbacHook = (params?: UseRbacHookParams) => {
  const selectedRoleId = params?.selectedRoleId ?? null;
  const {
    getRbacMatrix,
    getRoleById,
    createRole: createRoleService,
    updateRolePermissions,
    updateRolePermissionsRbac,
    duplicateRole: duplicateRoleService,
    getRoleUsers,
    assignUser,
    unassignUser,
    deleteRole: deleteRoleService,
  } = useRbacService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  const rbacQuery = useQuery<IServerResponse<TRbacMatrix>>({
    queryKey: ["rbac", "matrix", selectedInstitute?.id],
    queryFn: async () => {
      const response = await getRbacMatrix(selectedInstitute?.id);
      return response;
    },
    enabled: !!selectedInstitute?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const createRoleMutation = useMutation({
    mutationFn: async (payload: TCreateRoleRequest) => {
      if (!selectedInstitute?.id) throw new Error("No institute selected");
      const response = await createRoleService(selectedInstitute.id, payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rbac", "matrix", selectedInstitute?.id],
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (request: TUpdateRolePermissionsRequest) => {
      return await updateRolePermissions(request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rbac", "matrix", selectedInstitute?.id],
      });
    },
  });

  const roleByIdQuery = useQuery<IServerResponse<TRoleWithPermissions>>({
    queryKey: ["rbac", "role", selectedInstitute?.id, selectedRoleId],
    queryFn: async () => {
      if (!selectedInstitute?.id || !selectedRoleId)
        throw new Error("Missing institute or role");
      return getRoleById(selectedInstitute.id, selectedRoleId);
    },
    enabled: !!selectedInstitute?.id && !!selectedRoleId,
    staleTime: 60 * 1000,
  });

  const updateRbacMutation = useMutation({
    mutationFn: async ({
      roleId,
      permissions,
    }: {
      roleId: string;
      permissions: IRbac;
    }) => {
      if (!selectedInstitute?.id) throw new Error("No institute selected");
      return updateRolePermissionsRbac(
        selectedInstitute.id,
        roleId,
        permissions
      );
    },
    onSuccess: (_, { roleId }) => {
      queryClient.invalidateQueries({
        queryKey: ["rbac", "role", selectedInstitute?.id, roleId],
      });
      queryClient.invalidateQueries({
        queryKey: ["rbac", "matrix", selectedInstitute?.id],
      });
    },
  });

  const duplicateRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      if (!selectedInstitute?.id) throw new Error("No institute selected");
      return duplicateRoleService(selectedInstitute.id, roleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rbac", "matrix", selectedInstitute?.id],
      });
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: async (roleId: string) => {
      if (!selectedInstitute?.id) throw new Error("No institute selected");
      return deleteRoleService(selectedInstitute.id, roleId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["rbac", "matrix", selectedInstitute?.id],
      });
    },
  });

  const roles = rbacQuery.data?.data?.roles ?? [];

  return {
    ...rbacQuery,
    roles,
    roleWithPermissions: roleByIdQuery.data?.data ?? null,
    isLoadingRole: roleByIdQuery.isLoading,
    roleError: roleByIdQuery.error,
    createRole: createRoleMutation.mutateAsync,
    isCreatingRole: createRoleMutation.isPending,
    createRoleError: createRoleMutation.error,
    updateRolePermissions: updateMutation.mutateAsync,
    updateRolePermissionsRbac: updateRbacMutation.mutateAsync,
    isUpdating: updateMutation.isPending || updateRbacMutation.isPending,
    updateError: updateMutation.error ?? updateRbacMutation.error,
    duplicateRole: duplicateRoleMutation.mutateAsync,
    isDuplicating: duplicateRoleMutation.isPending,
    duplicateRoleError: duplicateRoleMutation.error,
    deleteRole: deleteRoleMutation.mutateAsync,
    isDeletingRole: deleteRoleMutation.isPending,
    deleteRoleError: deleteRoleMutation.error,
    getRoleUsers,
    assignUser,
    unassignUser,
  };
};

export type TRoleUsersQueryParams = {
  roleId: string;
  page: number;
  limit: number;
};

export const useRoleUsersHook = (params: TRoleUsersQueryParams | null) => {
  const { getRoleUsers, assignUser, unassignUser } = useRbacService();
  const queryClient = useQueryClient();
  const { selectedInstitute } = useInstituteContext();

  const roleUsersQuery = useQuery<IServerResponse<TRoleUsersResponse>>({
    queryKey: ["rbac", "roleUsers", selectedInstitute?.id, params?.roleId, params?.page, params?.limit],
    queryFn: async () => {
      if (!selectedInstitute?.id || !params?.roleId) throw new Error("Missing institute or role");
      return getRoleUsers(selectedInstitute.id, params.roleId, params.page, params.limit);
    },
    enabled: !!selectedInstitute?.id && !!params?.roleId,
    staleTime: 60 * 1000,
  });

  const invalidateRoleUsers = () => {
    queryClient.invalidateQueries({
      queryKey: ["rbac", "roleUsers", selectedInstitute?.id, params?.roleId],
    });
    queryClient.invalidateQueries({
      queryKey: ["rbac", "roleUserCount", selectedInstitute?.id, params?.roleId],
    });
  };

  const assignMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!selectedInstitute?.id || !params?.roleId) throw new Error("Missing institute or role");
      return assignUser(selectedInstitute.id, params.roleId, userId);
    },
    onSuccess: invalidateRoleUsers,
  });

  const unassignMutation = useMutation({
    mutationFn: async (userId: string) => {
      if (!selectedInstitute?.id || !params?.roleId) throw new Error("Missing institute or role");
      return unassignUser(selectedInstitute.id, params.roleId, userId);
    },
    onSuccess: invalidateRoleUsers,
  });

  return {
    ...roleUsersQuery,
    users: roleUsersQuery.data?.data?.users ?? [],
    total: roleUsersQuery.data?.data?.total ?? 0,
    assignedCount: roleUsersQuery.data?.data?.assignedCount ?? 0,
    page: roleUsersQuery.data?.data?.page ?? 1,
    limit: roleUsersQuery.data?.data?.limit ?? 20,
    assignUser: assignMutation.mutateAsync,
    unassignUser: unassignMutation.mutateAsync,
    isAssigning: assignMutation.isPending,
    isUnassigning: unassignMutation.isPending,
  };
};

export const useRoleUserCountsHook = (roleId: string | null) => {
  const { getRoleUsers } = useRbacService();
  const { selectedInstitute } = useInstituteContext();

  const query = useQuery({
    queryKey: ["rbac", "roleUserCount", selectedInstitute?.id, roleId],
    queryFn: async () => {
      if (!selectedInstitute?.id || !roleId)
        throw new Error("Missing institute or role");
      const res = await getRoleUsers(selectedInstitute.id, roleId, 1, 1);
      return res.data?.assignedCount ?? 0;
    },
    enabled: !!selectedInstitute?.id && !!roleId,
    staleTime: 60 * 1000,
  });

  const roleUserCounts: Record<string, number> =
    roleId && query.data != null ? { [roleId]: query.data } : {};

  return { roleUserCounts, isLoading: query.isLoading };
};
